import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (name) => {
    setActiveDropdown((prev) => (prev === name ? null : name));
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setActiveDropdown(null);
  };

  const navLinkClass = ({ isActive }) =>
    `px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
      isActive
        ? "bg-white/20 text-white shadow-sm"
        : "text-white/85 hover:bg-white/15 hover:text-white"
    }`;

  const dropdownItemClass = ({ isActive }) =>
    `block px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${
      isActive
        ? "bg-acem-surface-container-low text-acem-primary font-bold"
        : "text-acem-text hover:bg-acem-surface-container-low hover:text-acem-primary"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-acem-primary/95 backdrop-blur-md border-b border-white/10 text-white transition-all shadow-md" ref={navRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          onClick={closeMenu}
          className="flex items-center gap-2.5 text-white font-title text-xl font-bold tracking-wide hover:opacity-90 transition-opacity"
        >
          <img src="/logo.png" alt="ACEM Logo" className="h-9 w-auto object-contain drop-shadow" />
          <span>ACEM Fès</span>
        </Link>

        {/* Mobile menu toggle */}
        <button
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 focus:outline-none transition-colors"
          aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
          ) : (
            <Bars3Icon className="w-6 h-6" aria-hidden="true" />
          )}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/" end className={navLinkClass}>
            Accueil
          </NavLink>

          {/* Espace Études */}
          <div className="relative group">
            <button
              type="button"
              onClick={() => toggleDropdown('etudes')}
              className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                location.pathname.startsWith('/etudes') ? 'bg-white/20 text-white' : 'text-white/85 hover:bg-white/15 hover:text-white'
              }`}
            >
              <span>Espace études</span>
              <ChevronDownIcon className="w-4 h-4 transition-transform group-hover:rotate-180" aria-hidden="true" />
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform group-hover:translate-y-0 translate-y-1">
              <div className="w-60 bg-white rounded-lg shadow-acem-lg border border-acem-border p-2 space-y-1">
                <NavLink to="/etudes/guide-inscription" className={dropdownItemClass}>Guide Inscription</NavLink>
                <NavLink to="/etudes/liste-facultes" className={dropdownItemClass}>Liste des facultés</NavLink>
                <NavLink to="/etudes/parcours-universitaires" className={dropdownItemClass}>Parcours Universitaires</NavLink>
                <NavLink to="/etudes/bibliotheque" className={dropdownItemClass}>Bibliothèque PDF</NavLink>
              </div>
            </div>
          </div>

          {/* Événements */}
          <div className="relative group">
            <button
              type="button"
              onClick={() => toggleDropdown('evenements')}
              className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                location.pathname.startsWith('/evenements') ? 'bg-white/20 text-white' : 'text-white/85 hover:bg-white/15 hover:text-white'
              }`}
            >
              <span>Événements</span>
              <ChevronDownIcon className="w-4 h-4 transition-transform group-hover:rotate-180" aria-hidden="true" />
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform group-hover:translate-y-0 translate-y-1">
              <div className="w-60 bg-white rounded-lg shadow-acem-lg border border-acem-border p-2 space-y-1">
                <NavLink to="/evenements/communiques" className={dropdownItemClass}>Communiqués & Annonces</NavLink>
                <NavLink to="/evenements/calendrier" className={dropdownItemClass}>Calendrier Annuel</NavLink>
                <NavLink to="/evenements/a-venir" className={dropdownItemClass}>Événements à venir</NavLink>
                <NavLink to="/evenements/galerie" className={dropdownItemClass}>Galerie Photos</NavLink>
              </div>
            </div>
          </div>

          {/* Espace Social */}
          <div className="relative group">
            <button
              type="button"
              onClick={() => toggleDropdown('social')}
              className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                location.pathname.startsWith('/social') ? 'bg-white/20 text-white' : 'text-white/85 hover:bg-white/15 hover:text-white'
              }`}
            >
              <span>Espace Social</span>
              <ChevronDownIcon className="w-4 h-4 transition-transform group-hover:rotate-180" aria-hidden="true" />
            </button>
            <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform group-hover:translate-y-0 translate-y-1">
              <div className="w-64 bg-white rounded-lg shadow-acem-lg border border-acem-border p-2 space-y-1">
                <NavLink to="/social/entraide" className={dropdownItemClass}>Démarches administratives</NavLink>
                <NavLink to="/social/partenaires" className={dropdownItemClass}>Nos partenaires</NavLink>
                <NavLink to="/social/activites-sportives" className={dropdownItemClass}>Activités sportives</NavLink>
                <NavLink to="/social/repertoire" className={dropdownItemClass}>Répertoire utiles</NavLink>
                <NavLink to="/social/faq" className={dropdownItemClass}>Foire Aux Questions (FAQ)</NavLink>
              </div>
            </div>
          </div>

          {/* Nos Clubs */}
          <div className="relative group">
            <button
              type="button"
              onClick={() => toggleDropdown('clubs')}
              className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                location.pathname.startsWith('/culturel') ? 'bg-white/20 text-white' : 'text-white/85 hover:bg-white/15 hover:text-white'
              }`}
            >
              <span>Nos clubs</span>
              <ChevronDownIcon className="w-4 h-4 transition-transform group-hover:rotate-180" aria-hidden="true" />
            </button>
            <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform group-hover:translate-y-0 translate-y-1">
              <div className="w-64 bg-white rounded-lg shadow-acem-lg border border-acem-border p-2 space-y-1">
                <NavLink to="/culturel/club-lecture" className={dropdownItemClass}>Club de lecture</NavLink>
                <NavLink to="/culturel/club-robotique" className={dropdownItemClass}>Club de robotique</NavLink>
                <NavLink to="/culturel/club-danse" className={dropdownItemClass}>Club de danses traditionnelles</NavLink>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Drawer Menu */}
      {menuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 w-full bg-acem-primary-dark/95 backdrop-blur-xl border-b border-white/10 px-4 pt-3 pb-6 space-y-2 shadow-2xl z-50 animate-slide-up max-h-[calc(100vh-4rem)] overflow-y-auto">
          <NavLink
            to="/"
            end
            onClick={closeMenu}
            className="block px-3 py-2 rounded-md text-base font-semibold hover:bg-white/10 text-white"
          >
            Accueil
          </NavLink>

          {/* Mobile Accordion - Espace études */}
          <div>
            <button
              type="button"
              onClick={() => toggleDropdown('m-etudes')}
              className="w-full flex justify-between items-center px-3 py-2 text-base font-semibold text-white hover:bg-white/10 rounded-md"
            >
              <span>Espace études</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${activeDropdown === 'm-etudes' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {activeDropdown === 'm-etudes' && (
              <div className="pl-4 mt-1 space-y-1 border-l-2 border-white/20">
                <NavLink to="/etudes/guide-inscription" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Guide Inscription</NavLink>
                <NavLink to="/etudes/liste-facultes" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Liste des facultés</NavLink>
                <NavLink to="/etudes/parcours-universitaires" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Parcours Universitaires</NavLink>
                <NavLink to="/etudes/bibliotheque" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Bibliothèque PDF</NavLink>
              </div>
            )}
          </div>

          {/* Mobile Accordion - Événements */}
          <div>
            <button
              type="button"
              onClick={() => toggleDropdown('m-evenements')}
              className="w-full flex justify-between items-center px-3 py-2 text-base font-semibold text-white hover:bg-white/10 rounded-md"
            >
              <span>Événements</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${activeDropdown === 'm-evenements' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {activeDropdown === 'm-evenements' && (
              <div className="pl-4 mt-1 space-y-1 border-l-2 border-white/20">
                <NavLink to="/evenements/communiques" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Communiqués & Annonces</NavLink>
                <NavLink to="/evenements/calendrier" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Calendrier Annuel</NavLink>
                <NavLink to="/evenements/a-venir" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Événements à venir</NavLink>
                <NavLink to="/evenements/galerie" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Galerie Photos</NavLink>
              </div>
            )}
          </div>

          {/* Mobile Accordion - Espace Social */}
          <div>
            <button
              type="button"
              onClick={() => toggleDropdown('m-social')}
              className="w-full flex justify-between items-center px-3 py-2 text-base font-semibold text-white hover:bg-white/10 rounded-md"
            >
              <span>Espace Social</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${activeDropdown === 'm-social' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {activeDropdown === 'm-social' && (
              <div className="pl-4 mt-1 space-y-1 border-l-2 border-white/20">
                <NavLink to="/social/entraide" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Démarches administratives</NavLink>
                <NavLink to="/social/partenaires" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Nos partenaires</NavLink>
                <NavLink to="/social/activites-sportives" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Activités sportives</NavLink>
                <NavLink to="/social/repertoire" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Répertoire utiles</NavLink>
                <NavLink to="/social/faq" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Foire Aux Questions (FAQ)</NavLink>
              </div>
            )}
          </div>

          {/* Mobile Accordion - Nos Clubs */}
          <div>
            <button
              type="button"
              onClick={() => toggleDropdown('m-clubs')}
              className="w-full flex justify-between items-center px-3 py-2 text-base font-semibold text-white hover:bg-white/10 rounded-md"
            >
              <span>Nos clubs</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${activeDropdown === 'm-clubs' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {activeDropdown === 'm-clubs' && (
              <div className="pl-4 mt-1 space-y-1 border-l-2 border-white/20">
                <NavLink to="/culturel/club-lecture" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Club de lecture</NavLink>
                <NavLink to="/culturel/club-robotique" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Club de robotique</NavLink>
                <NavLink to="/culturel/club-danse" onClick={closeMenu} className="block px-3 py-2 text-sm text-white/90 hover:text-white">Club de danses traditionnelles</NavLink>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
